import { useRef, useCallback, useEffect, useMemo } from "react";
import {
  createIntersectionCallback,
  createIntersectionObserver,
  checkIntersectionObserverSupport,
  resetChildAccessibility
} from "../utils";

const useChildAccessibility = (listRef, isNotMobileViewport) => {
  const observerRef = useRef(null);
  const isObserverActiveRef = useRef(false);

  const intersectionCallback = useMemo(() => createIntersectionCallback(), []);

  const resetObserver = useCallback(() => {
    if (isObserverActiveRef.current && listRef.current) {
      resetChildAccessibility(listRef.current, observerRef.current);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      isObserverActiveRef.current = false;
    }
  }, [listRef]);

  const observeChildren = useCallback(() => {
    if (!listRef.current || !observerRef.current) return;

    const children = listRef.current.children;

    Array.from(children).forEach(child => {
      observerRef.current.observe(child);
    });
    isObserverActiveRef.current = true;
  }, [listRef]);

  const createObserver = useCallback(() => {
    if (!observerRef.current && listRef.current) {
      observerRef.current = createIntersectionObserver(
        listRef.current,
        intersectionCallback
      );
    }
  }, [listRef, intersectionCallback]);

  const shouldUseObserver = useCallback(
    () => isNotMobileViewport && checkIntersectionObserverSupport(),
    [isNotMobileViewport]
  );

  const updateExistingObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observeChildren();
    }
  }, [observeChildren]);

  const initializeObserver = useCallback(() => {
    createObserver();
    if (observerRef.current) {
      observeChildren();
    }
  }, [createObserver, observeChildren]);

  const handleChildAccessibility = useCallback(() => {
    if (!listRef.current) return;

    if (!shouldUseObserver()) {
      resetObserver();

      return;
    }

    if (isObserverActiveRef.current) {
      updateExistingObserver();
    } else {
      initializeObserver();
    }
  }, [
    listRef,
    shouldUseObserver,
    resetObserver,
    updateExistingObserver,
    initializeObserver
  ]);

  useEffect(
    () => () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    },
    []
  );

  return { handleChildAccessibility, observerRef };
};

export default useChildAccessibility;
